<!-- Componente para hacer calculos  -->
<script>
  import { tick } from "svelte";
  import { ButtonV2, CardV2, TextField, Snackbar } from "$lib";
  import { _ } from "$helpers/i18n.js";
  import { calculateCashflowMonthly, FormatNumber } from "../../../helpers/calculator";
  import { globalStore } from "../../store/globalStore";

  const { country } = globalStore;

  let initialBalance = null;

  let income = {
    client: null,
    loans: null,
    additional: null
  };

  let expenses = {
    salaries: null,
    services: null,
    providers: null,
    equipment: null,
    publicity: null,
    platform: null,
    debts: null,
    accounts: null,
    additionalExpenses: null
  };

  let result = null;

  $: initialBalance,
     income.client, income.loans, income.additional,
     expenses.salaries, expenses.services, expenses.providers, expenses.equipment,
     expenses.publicity, expenses.platform, expenses.debts, expenses.accounts, expenses.additionalExpenses,
     (result = null);

  let resultEl;

  // Variables para el Snackbar
  let showSnackbar = false;
  let snackbarMessage = "";

  // Función para sumar valores
  function sumValues(obj) {
    let total = 0;
    for (const val of Object.values(obj)) {
      total += parseFloat(val) || 0;
    }
    return total;
  }

  async function calculate() {
    // Resetear snackbar
    showSnackbar = false;

    if (initialBalance < 0) {
      snackbarMessage = $_("snackbar.calculation.positive_initial_balance");
      showSnackbar = true;
      return;
    }

    // Validar ingresos
    for (const val of Object.values(income)) {
      if (val < 0) {
        snackbarMessage = $_("snackbar.calculation.positive_income_values");
        showSnackbar = true;
        return;
      }
    }

    // Validar egresos
    for (const val of Object.values(expenses)) {
      if (val < 0) {
        snackbarMessage = $_("snackbar.calculation.positive_expense_values");
        showSnackbar = true;
        return;
      }
    }

    const totalIncome = sumValues(income);
    const totalExpenses = sumValues(expenses);
    const balance = parseFloat(initialBalance) || 0;

    result = calculateCashflowMonthly(balance, totalIncome, totalExpenses, $country).result;

    await tick();
    resultEl?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }
</script>

<CardV2 variant="outlined">
  <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
    <p>{$_("cashflowMonthly.calculation.first_input")}</p>
    <TextField
      bind:value={initialBalance}
      placeholder="0"
      leadingText={$_("common.currency_symbol")}
      type="number"
      step="0.01"
      min={0}
      testId="cashflowDMonthlyInitialBalanceInput"
    />
  </div>
  <CardV2 variant="outlined">
    <h3 class="cashflow-m-section-title">{$_("cashflowMonthly.calculation.income_title")}</h3>

    <div class="row" on:keydown={(event) => event.key == '-' && event.preventDefault()}>
      <div>
        <p>{$_("cashflowMonthly.calculation.income_1")}</p>
        <TextField
          bind:value={income.client}
          placeholder="0"
          leadingText={$_("common.currency_symbol")}
          type="number"
          step="0.01"
          min={0}
          testId="cashflowDMonthlyIncomeInput"
        />
      </div>

      <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
        <p>{$_("cashflowMonthly.calculation.income_2")}</p>
        <TextField
          bind:value={income.loans}
          placeholder="0"
          leadingText={$_("common.currency_symbol")}
          type="number"
          step="0.01"
          min={0}
          testId="cashflowDMonthlyLoansInput"
        />
      </div>
    </div>

    <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
      <p>{$_("cashflowMonthly.calculation.income_3")}</p>
      <TextField
        bind:value={income.additional}
        placeholder="0"
        leadingText={$_("common.currency_symbol")}
        type="number"
        step="0.01"
        min={0}
        testId="cashflowDMonthlyAdditionalIncomeInput"
      />
    </div>
  </CardV2>

  <CardV2 variant="outlined">
    <h3 class="cashflow-m-section-title">{$_("cashflowMonthly.calculation.expenses_title")}</h3>

    <div class="row" on:keydown={(event) => event.key == '-' && event.preventDefault()}>
      <div>
        <p>{$_("cashflowMonthly.calculation.expenses_1")}</p>
        <TextField
          bind:value={expenses.salaries}
          placeholder="0"
          leadingText={$_("common.currency_symbol")}
          type="number"
          step="0.01"
          min={0}
          testId="cashflowDMonthlyExpensesInput"
        />
      </div>
      <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
        <p>{$_("cashflowMonthly.calculation.expenses_2")}</p>
        <TextField
          bind:value={expenses.services}
          placeholder="0"
          leadingText={$_("common.currency_symbol")}
          type="number"
          step="0.01"
          min={0}
          testId="cashflowDMonthlyServicesInput"
        />
      </div>
      <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
        <p>{$_("cashflowMonthly.calculation.expenses_3")}</p>
        <TextField
          bind:value={expenses.providers}
          placeholder="0"
          leadingText={$_("common.currency_symbol")}
          type="number"
          step="0.01"
          min={0}
          testId="cashflowDMonthlyProvidersInput"
        />
      </div>
      <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
        <p>{$_("cashflowMonthly.calculation.expenses_4")}</p>
        <TextField
          bind:value={expenses.equipment}
          placeholder="0"
          leadingText={$_("common.currency_symbol")}
          type="number"
          step="0.01"
          min={0}
          testId="cashflowDMonthlyEquipmentInput"
        />
      </div>
      <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
        <p>{$_("cashflowMonthly.calculation.expenses_5")}</p>
        <TextField
          bind:value={expenses.publicity}
          placeholder="0"
          leadingText={$_("common.currency_symbol")}
          type="number"
          step="0.01"
          min={0}
          testId="cashflowDMonthlyPublicityInput"
        />
      </div>
      <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
        <p>{$_("cashflowMonthly.calculation.expenses_6")}</p>
        <TextField
          bind:value={expenses.platform}
          placeholder="0"
          leadingText={$_("common.currency_symbol")}
          type="number"
          step="0.01"
          min={0}
          testId="cashflowDMonthlyPlatformInput"
        />
      </div>
      <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
      <p>{$_("cashflowMonthly.calculation.expenses_7")}</p>
      <TextField
        bind:value={expenses.debts}
        placeholder="0"
        leadingText={$_("common.currency_symbol")}
        type="number"
        step="0.01"
        min={0}
        testId="cashflowDMonthlyDebtsInput"
      />
    </div>
      <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
        <p>{$_("cashflowMonthly.calculation.expenses_8")}</p>
        <TextField
          bind:value={expenses.accounts}
          placeholder="0"
          leadingText={$_("common.currency_symbol")}
          type="number"
          step="0.01"
          min={0}
          testId="cashflowDMonthlyAccountsInput"
          />
      </div>
    </div>
    <div on:keydown={(event) => event.key == '-' && event.preventDefault()}>
      <p>{$_("cashflowMonthly.calculation.expenses_9")}</p>
      <TextField
        bind:value={expenses.additionalExpenses}
        placeholder="0"
        leadingText="$"
        type="number"
        step="0.01"
      />
    </div>
  </CardV2>

  <ButtonV2 variant="primary" class="btn-principal" on:click={calculate} dataTrack="calculate_cashflow_monthly">
    {$_("common.calculate_button")}
  </ButtonV2>

  {#if result != null}
    <div class="result-static" class:negative={result < 0} data-testid="cashflowMCalculationResult" bind:this={resultEl}>
      <p>{$_("cashflowMonthly.calculation.result")}</p>
      {FormatNumber($country, result)}
    </div>
  {/if}

  <Snackbar 
    variant="warning" 
    visible={showSnackbar} 
    message={snackbarMessage} 
    on:close={() => (showSnackbar = false)}
  />
</CardV2>

<style>  
  /* Ajustes responsive para pantallas pequeñas */
  @media (max-width: 480px) {
    :global(.bs-card) {
      padding: 16px !important;
      row-gap: 16px !important;
    }

    .row {
      gap: 16px !important;
    }

    p {
      padding: 4px 0 6px 0;
    }
  }
</style>